// frontend/src/pages/PublicReservationPage/PublicReservationPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Calendar from '../../components/Calendar/Calendar';
import PublicReservationForm from '../../components/PublicReservationForm/PublicReservationForm';
import { getPublicCalendarData } from '../../services/reservationService';
import { getClosures, getFacilityInfo } from '../../services/facilityService';
import { getCalendarBlocks } from '../../services/calendarBlockService';
import styles from './PublicReservationPage.module.css';

const generateDateRange = (startDateStr, endDateStr) => {
    const dates = [];
    const start = new Date(startDateStr + 'T00:00:00Z');
    const end = new Date(endDateStr + 'T00:00:00Z');
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return dates;
    let current = new Date(start);
    while (current <= end) {
        const year = current.getUTCFullYear();
        const month = String(current.getUTCMonth() + 1).padStart(2, '0');
        const day = String(current.getUTCDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        current.setUTCDate(current.getUTCDate() + 1);
    }
    return dates;
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const PublicReservationPage = () => {
    const [calendarDisplayDate, setCalendarDisplayDate] = useState(new Date());
    const [allReservations, setAllReservations] = useState([]);
    const [closures, setClosures] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [facility, setFacility] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDates, setSelectedDates] = useState([]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        const year = calendarDisplayDate.getFullYear();
        const month = calendarDisplayDate.getMonth() + 1;

        try {
            const startOfRange = new Date(Date.UTC(year, month - 2, 1));
            const endOfRange = new Date(Date.UTC(year, month + 1, 1));

            const [resData, closureData, facilityData, blocksData] = await Promise.all([
                getPublicCalendarData(year, month),
                getClosures({ start: startOfRange.toISOString(), end: endOfRange.toISOString() }),
                getFacilityInfo(),
                getCalendarBlocks()
            ]);

            setAllReservations(resData || []);
            setClosures(closureData || []);
            setFacility(facilityData);
            setBlocks(blocksData || []);
        } catch (err) {
            setError(err.message || `Could not load availability data.`);
        } finally {
            setIsLoading(false);
        }
    }, [calendarDisplayDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const publicReservedDatesMap = useMemo(() => {
        const map = new Map();

        if (allReservations && allReservations.length > 0) {
            allReservations.forEach(res => {
                const startDate = res.startTime?.split('T')[0];
                const endDate = res.endTime?.split('T')[0];
                const churchName = res.account?.accountName || 'Church';
                let statusLabel = res.status === 'pending' ? 'Pending' : 'Reserved';

                if (startDate && endDate) {
                    const datesInRange = generateDateRange(startDate, endDate);
                    datesInRange.forEach(dateStr => {
                        map.set(dateStr, {
                            type: 'publicReserved',
                            statusLabel: statusLabel,
                            churchName: churchName,
                            status: res.status
                        });
                    });
                }
            });
        }

        if (closures && closures.length > 0) {
            closures.forEach(c => {
                const start = c.startTime.split('T')[0];
                const end = c.endTime.split('T')[0];
                if (start && end) {
                    generateDateRange(start, end).forEach(d => {
                        map.set(d, {
                            type: 'closure',
                            text: 'Unavailable',
                            status: 'closed'
                        });
                    });
                }
            });
        }

        return map;
    }, [allReservations, closures]);

    const handleMonthChange = (newDate) => {
        setCalendarDisplayDate(newDate);
    };

    const handleDateSelect = (payload) => {
        // 1. Check if payload is a block object
        if (typeof payload === 'object' && payload.startDate && payload.endDate) {
            const { startDate, endDate } = payload;

            // Block the selection if any date in the block is already reserved/pending
            const blockDates = generateDateRange(startDate, endDate);
            const isBlockTaken = blockDates.some(d => publicReservedDatesMap.has(d));
            if (isBlockTaken) return;

            // Toggle logic for the block
            if (selectedDates.length > 0 && selectedDates[0] === startDate && selectedDates[selectedDates.length - 1] === endDate) {
                setSelectedDates([]);
            } else {
                setSelectedDates(blockDates);
            }
            return;
        }

        // 2. Handle as free selection (string payload)
        // If in blocks mode, ignore individual day clicks
        if (facility?.selectionMode === 'blocks') {
            return;
        }

        const clickedDateStr = payload;

        // Prevent selecting a date that is already reserved or pending
        if (publicReservedDatesMap.has(clickedDateStr)) return;

        setSelectedDates(prevSelectedDates => {
            const sortedSelection = [...prevSelectedDates].sort();
            const selectionLength = sortedSelection.length;
            if (selectionLength === 0) return [clickedDateStr];
            if (selectionLength === 1) {
                const [start, end] = [sortedSelection[0], clickedDateStr].sort();
                if (start === end) return [];
                return generateDateRange(start, end);
            }
            return [clickedDateStr];
        });
    };

    const handleFormClose = () => {
        setSelectedDates([]);
    };

    const handleFormSuccess = () => {
        alert("Reservation request submitted successfully!");
        setSelectedDates([]);
        fetchData();
    };

    return (
        <div className={styles.pageContainer}>
            <h1 className="page-main-title">Reservation Request & Availability</h1>
            <p className={styles.pageInstructions}>
                {facility?.selectionMode === 'blocks'
                    ? 'View availability and select a highlighted block on the calendar to request your reservation.'
                    : 'View availability and select dates on the calendar to request a reservation at Highview House.'}
            </p>

            {error && <p className={styles.errorMessage}>{error}</p>}

            <div className={styles.calendarWrapper}>
                <Calendar
                    currentDisplayDate={calendarDisplayDate}
                    onMonthChange={handleMonthChange}
                    reservedDatesMap={publicReservedDatesMap}
                    isLoading={isLoading}
                    onDateSelect={handleDateSelect}
                    selectedDates={selectedDates}
                    isReadOnly={false}
                    selectionMode={facility?.selectionMode || 'free'}
                    blocks={blocks}
                />
            </div>

            {selectedDates.length > 0 && (
                <div className={styles.formSection}>
                    <PublicReservationForm
                        selectedDates={selectedDates}
                        onClose={handleFormClose}
                        onSuccess={handleFormSuccess}
                        isInline={true}
                    />
                </div>
            )}
        </div>
    );
};

export default PublicReservationPage;
